import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type {
    ContentRef,
    CustomizationContentLink,
    CustomizationHeaderItem,
} from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Fieldset,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { ContentReferenceInputField } from '../../ContentReferences';
import { InvalidSpaceLinkAlert } from './InvalidSpaceLink';
import { LinkStyleField } from './LinkStyleField';
import { isContentRefBroken } from './util';

export type ItemType = 'link' | 'dropdown';

type Item = CustomizationContentLink | CustomizationHeaderItem;
type ItemFromType<TItemType extends ItemType> = TItemType extends 'link'
    ? CustomizationContentLink
    : CustomizationHeaderItem;

export function AddLinkDialog<TItemType extends ItemType>(props: {
    onAdd: (item: ItemFromType<TItemType>) => void;
    title: string;
    itemType: TItemType;
    siteSpaceIds: string[];
}) {
    const { onAdd, title, itemType, siteSpaceIds } = props;
    return (
        <Dialog>
            <DialogHeader>
                <Heading>{title}</Heading>
            </DialogHeader>
            <LinkForm onSubmit={onAdd} itemType={itemType} siteSpaceIds={siteSpaceIds} />
        </Dialog>
    );
}

export function EditLinkDialog<TItemType extends ItemType>(props: {
    onChange: (item: ItemFromType<TItemType>) => void;
    title: string;
    item: Item;
    itemType: TItemType;
    siteSpaceIds: string[];
}) {
    const { onChange, item, itemType, siteSpaceIds } = props;
    // Freeze the title to prevent it from changing when the item changes
    const [title] = React.useState(props.title);
    return (
        <Dialog>
            <DialogHeader>
                <Heading>{title}</Heading>
            </DialogHeader>
            <LinkForm
                onSubmit={onChange}
                item={item}
                itemType={itemType}
                siteSpaceIds={siteSpaceIds}
            />
        </Dialog>
    );
}

type LinkFormInputs = {
    title: string;
    to: ContentRef | null;
    style: CustomizationHeaderItem['style'] | undefined;
};

function LinkForm<TItemType extends ItemType>(props: {
    onSubmit: (item: ItemFromType<TItemType>) => void;
    item?: Item;
    itemType: TItemType;
    siteSpaceIds: string[];
}) {
    const closeDialog = useDialogClose();
    const form = useForm<LinkFormInputs>({
        defaultValues: {
            title: props.item?.title ?? '',
            to: props.item && 'to' in props.item && props.item.to ? props.item.to : null,
            style:
                props.item && 'style' in props.item
                    ? props.item.style
                    : props.itemType === 'dropdown'
                      ? 'link'
                      : undefined,
        },
    });

    const onSubmit: SubmitHandler<LinkFormInputs> = (data) => {
        const submitted: CustomizationHeaderItem | CustomizationContentLink = (() => {
            switch (props.itemType) {
                case 'dropdown': {
                    assert(data.style);
                    return {
                        title: data.title,
                        style: data.style,
                        links: props.item && 'links' in props.item ? props.item.links : [],
                        to: data.to,
                    };
                }
                case 'link': {
                    assert(data.to);
                    return {
                        title: data.title,
                        to: data.to,
                    };
                }
                default:
                    assertNever(props.itemType);
            }
        })();

        props.onSubmit(submitted as ItemFromType<TItemType>);
        closeDialog();
    };

    const to = form.watch('to');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <TextField name="title" required>
                        <Label>Title</Label>
                        <Description>
                            The name of the link displayed in the navigation menu. Keep it short and
                            descriptive for easy identification.
                        </Description>
                        <TextInput autoFocus placeholder="Go to website" />
                        <FieldError />
                    </TextField>
                    <FieldGroup>
                        <Label>Link</Label>
                        <Description>Enter a URL or link to some content.</Description>
                        <ContentReferenceInputField
                            name="to"
                            control={form.control}
                            size="medium"
                            placeholder="Search for content or type a URL..."
                            accept={[
                                'space',
                                'page',
                                'anchor',
                                'url',
                                'reusable-content',
                                'openapi',
                            ]}
                            required={props.itemType !== 'dropdown'}
                        />
                    </FieldGroup>
                    {(() => {
                        switch (props.itemType) {
                            case 'dropdown': {
                                if (!to) {
                                    return null;
                                }
                                return <LinkStyleField name="style" />;
                            }
                            case 'link':
                                return null;
                            default:
                                assertNever(props.itemType);
                        }
                    })()}
                </Fieldset>
                {isContentRefBroken(to, props.siteSpaceIds) ? <InvalidSpaceLinkAlert /> : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit
                    disabled={isContentRefBroken(to, props.siteSpaceIds)}
                    label={props.item ? 'Update' : 'Add'}
                />
            </DialogFooter>
        </Form>
    );
}
